import React from 'react';
import { Button, FlexRow, Grid, ISelectOption, ISpruceFieldProps, Select, SpruceField } from 'ui-toolkit';
import { IDefaultContactRoles } from '../../types/clients/CreateDefaultContact';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLevelUpAlt } from '@fortawesome/pro-solid-svg-icons';
import {
  clientUSAStateOptions,
  eContactAdditionalStateOptions,
  IContactUSAStates,
} from '../../types/contacts/IContactUSAStates';
import { useListRolesQuery } from '../../API/roles/RolesApi';
import { rolesOptions } from '../../types/roles/rolesOptions';
import { IRoleData } from '../../types/roles/IRole';

export const DefaultContactRolesField: React.FC<{ isValid: boolean }> = ({ isValid }) => {
  const { data: roles = [] } = useListRolesQuery();

  const getStateValue = (values?: Array<IContactUSAStates>): ISelectOption[] => {
    return values && values.length > 0
      ? values.map((value) => clientUSAStateOptions.find((o) => o.value === value)!)
      : [];
  };

  const onRoleChange = (
    roles: IDefaultContactRoles,
    value: string | null,
    oldRole: string,
    onChange: (value: any) => void,
  ) => {
    const updatedRoles = Object.fromEntries(
      Object.entries(roles).map(([key, val]) =>
        key === oldRole ? [value || '', val] : [key, val],
      ),
    );

    onChange(updatedRoles);
  };

  const onStateChange = (
    rolesValue: any,
    stateValues: ISelectOption[],
    contactRole: string,
    onChange: (value: any) => void,
  ) => {
    const additionalStateOption = stateValues.find((s) =>
      [eContactAdditionalStateOptions.AllStates, eContactAdditionalStateOptions.AllOtherStates].includes(s.value),
    );
    const states = additionalStateOption ? [additionalStateOption.value] : stateValues.map((value) => value.value);
    onChange({
      ...rolesValue,
      [contactRole]: states,
    });
  };

  const onAddNewRole = (rolesValue: any, onChange: (value: any) => void) => {
    onChange({
      ...rolesValue,
      '': [],
    });
  };

  const onRemoveRole = (rolesValue: any, contactRole: string, onChange: (value: any) => void) => {
    const newValue = Object.fromEntries(
      Object.entries(rolesValue).filter(([key]) => key !== contactRole),
    );

    onChange(newValue);
  };

  return (
    <SpruceField name={'roles'}>
      {({ field }: ISpruceFieldProps) => (
        <>
          {Object.keys(field.value).map((contactRole) => (
            <>
              <Select
                errorText={null}
                onChange={(value: ISelectOption) =>
                  onRoleChange(field.value, value ? value.value : null, contactRole, field.onChange)
                }
                value={rolesOptions(roles).find(
                  (option) => option.value === contactRole,
                )}
                options={availableRolesOptions(field.value, roles)}
                isClearable={true}
                label={'Role'}
              />
              {contactRole && (
                <Grid templateColumns={'min-content 1fr'}>
                  <RotatedIcon icon={faLevelUpAlt} />

                  <Select
                    errorText={null}
                    onChange={(values: ISelectOption[]) =>
                      onStateChange(field.value, values, contactRole, field.onChange)
                    }
                    value={getStateValue(field.value[contactRole])}
                    options={clientUSAStateOptions}
                    isClearable={true}
                    isMulti={true}
                    label={'States'}
                  />
                </Grid>
              )}
              <FlexRow justifyContent={'flex-end'}>
                <Button intent={'ghost'} onClick={() => onRemoveRole(field.value, contactRole, field.onChange)}>
                  Remove
                </Button>
              </FlexRow>
            </>
          ))}
          <FlexRow>
            <Button
              intent={'secondary'}
              disabled={!isValid}
              onClick={() => onAddNewRole(field.value, field.onChange)}
            >
              + Add Role
            </Button>
          </FlexRow>
        </>
      )}
    </SpruceField>
  );
};

const RotatedIcon = styled(FontAwesomeIcon)`
    transform: rotate(90deg);
    ${(props) => props.theme.fonts.header2Size};
`;

const availableRolesOptions = (existingRoles: IDefaultContactRoles, roles: IRoleData[]): ISelectOption[] => {
  return rolesOptions(roles).filter(
    (value) => !Object.keys(existingRoles).includes(value.value),
  );
};
